import React, { useContext } from "react";

import MissingError from "./missing-error";
import { RouteContext } from "./router";

type MissingBoundaryProps = {
  /**
   * Fallback component to render when a missing error occurs
   */
  fallback: (error: MissingError) => React.ReactNode;
  /**
   * Child components to render when no error occurs
   */
  children: React.ReactNode;
};

type RouteContextType = {
  history: {
    location: {
      pathname: string;
    };
  };
} | null;

type InnerBoundaryProps = MissingBoundaryProps & {
  routeContext: RouteContextType;
};

type InnerBoundaryState = {
  error?: MissingError;
  errorInfo?: React.ErrorInfo;
  errorPathname?: string;
};

const MissingBoundary = (props: MissingBoundaryProps) => {
  const routeContext = useContext(RouteContext) as RouteContextType;

  return <InnerBoundary routeContext={routeContext} {...props} />;
};

class InnerBoundary extends React.Component<
  InnerBoundaryProps,
  InnerBoundaryState
> {
  constructor(props: InnerBoundaryProps) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromProps(
    props: InnerBoundaryProps,
    state: InnerBoundaryState,
  ): Partial<InnerBoundaryState> | null {
    if (
      state.error &&
      props.routeContext &&
      props.routeContext.history.location.pathname !== state.errorPathname
    ) {
      return {
        error: undefined,
        errorInfo: undefined,
        errorPathname: undefined,
      };
    }

    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (error instanceof MissingError && this.props.routeContext) {
      this.setState({
        error,
        errorInfo: info,
        errorPathname: this.props.routeContext.history.location.pathname,
      });
    }
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.state.error);
    }

    return this.props.children;
  }
}

export default MissingBoundary;
